import type { Fragment, NodeType } from '@tiptap/pm/model';

/**
 * Splits a paragraph's inline content at its first line break: content before
 * the break becomes `headingContent`, everything after becomes `bodyContent`
 * (`null` when there is no break).
 *
 * A line break is either a `hardBreak` node or a literal `\n` inside a text
 * node. The markdown-native editor keeps a single `\n` as a soft break inside
 * one text node (e.g. `**Objective**\nChest X-ray…` parses to one paragraph
 * with a `\n`), while a `  \n` hard break becomes a `hardBreak` node. Both mark
 * the boundary between a header line and its body, so promoting that line to a
 * section must keep only the pre-break text as the heading and carry the rest
 * into the body, instead of sweeping the whole paragraph into the heading.
 */
export function splitContentAtLineBreak(
  content: Fragment,
  hardBreakType: NodeType | undefined
): { headingContent: Fragment; bodyContent: Fragment | null } {
  let breakOffset = -1;
  let breakSize = 0;
  let offset = 0;
  content.forEach((node) => {
    if (breakOffset !== -1) return;
    if (node.isText && node.text) {
      const newlineIndex = node.text.indexOf('\n');
      if (newlineIndex !== -1) {
        breakOffset = offset + newlineIndex;
        breakSize = 1;
      }
    } else if (hardBreakType && node.type === hardBreakType) {
      breakOffset = offset;
      breakSize = node.nodeSize;
    }
    offset += node.nodeSize;
  });

  if (breakOffset === -1) return { headingContent: content, bodyContent: null };

  return {
    headingContent: content.cut(0, breakOffset),
    bodyContent: content.cut(breakOffset + breakSize),
  };
}
